import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OclLogo from '../layout/OclLogo';
import { WHITE } from '../../common/colors';
import { oclProfileURL } from '../../common/constants';

// Blog bylines (ocl_online#219). `authors` is a post's resolved list from
// posts.json (build-blog-index.js resolveAuthors): { id, name, title, ocl,
// avatar, bio }. An empty list is an Open Concept Lab post.
//
// Person avatars follow TestimonialCard (home/Testimonials.jsx): MUI Avatar
// with initials when there's no picture. The organization avatar is
// oclweb3's CommunityBlog one: the OCL mark in white on primary. The byline
// isn't a design-system component yet; propose it there once it settles.
const ORG_NAME = 'Open Concept Lab';
const NAME_SX = { color: 'surface.dark', fontWeight: 700 };

export const initials = (name) => name.replace(/^Dr\.\s+/, '').split(/\s+/).filter(Boolean)
  .map(word => word[0]).slice(0, 2).join('').toUpperCase();

export const AuthorAvatar = ({ author, size }) => author ? (
  <Avatar
    src={author.avatar || undefined}
    alt=""
    aria-hidden="true"
    sx={{ width: size, height: size, bgcolor: 'primary.60', fontSize: size * 0.36, '& img': { objectPosition: 'top' } }}>
    {initials(author.name)}
  </Avatar>
) : (
  <Avatar alt="" aria-hidden="true" sx={{ width: size, height: size, bgcolor: 'primary.main' }}>
    <OclLogo color={WHITE} height={Math.round(size * 0.28)} />
  </Avatar>
);

const Avatars = ({ authors, size }) => {
  if (authors.length < 2)
    return <AuthorAvatar author={authors[0]} size={size} />;
  return (
    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: size, height: size, borderWidth: 2 } }}>
      {authors.map(author => <AuthorAvatar key={author.id} author={author} size={size} />)}
    </AvatarGroup>
  );
};

// Anonymous readers land on OCL Online's sign-in page; accepted (spec).
const ProfileLink = ({ username }) => {
  const { t } = useTranslation();
  return (
    <Box
      component="a"
      href={oclProfileURL(username)}
      target="_blank"
      rel="noopener noreferrer"
      title={t('blog.profile_link_title')}
      aria-label={t('blog.profile_link_label', { username })}
      sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
      @{username}
    </Box>
  );
};

// "A", "A and B", "A, B and C" — each item a node, so names can stay bold.
const joined = (items, and) => items.map((item, index) => (
  <React.Fragment key={index}>
    {index > 0 && (index === items.length - 1 ? ` ${and} ` : ', ')}
    {item}
  </React.Fragment>
));

// Post page: avatars, then name, title, @handle, date and read time on one
// line (wrapping only when the screen is too narrow). `date` and `readTime`
// are separate nowrap spans, not one "date · N min read" string, so a long
// locale's date can't force the whole tail to overflow a narrow screen. Each
// separator's leading space sits outside its nowrap span, not inside it, so
// the line has a break opportunity before each "·" while the "· text" itself
// stays whole.
export const Byline = ({ authors, date, readTime }) => {
  const { t } = useTranslation();
  const people = authors.map(author => (
    <React.Fragment key={author.id}>
      <Box component="span" sx={NAME_SX}>{author.name}</Box>
      {`, ${author.title}`}
      {author.ocl && <>{' '}<ProfileLink username={author.ocl} /></>}
    </React.Fragment>
  ));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Avatars authors={authors} size={32} />
      <Typography variant="body2" sx={{ color: 'surface.contrastText', lineHeight: 1.5 }}>
        {authors.length ? joined(people, t('blog.byline_and')) : <Box component="span" sx={NAME_SX}>{ORG_NAME}</Box>}
        {' '}<Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`· ${date}`}</Box>
        {' '}<Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`· ${readTime}`}</Box>
      </Typography>
    </Box>
  );
};

// Blog card: small avatars and names under the excerpt. PostCard renders it
// for person-authored posts only.
export const CardAuthors = ({ authors }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
      <Avatars authors={authors} size={24} />
      <Typography variant="body2" noWrap sx={{ color: 'surface.dark', fontWeight: 600, fontSize: '0.8125rem', minWidth: 0 }}>
        {joined(authors.map(author => author.name), t('blog.byline_and'))}
      </Typography>
    </Box>
  );
};

// End of post: one panel per author who has a bio. Without a bio the panel
// would only repeat the byline, so those authors are left out.
export const AboutAuthors = ({ authors }) => {
  const { t } = useTranslation();
  const withBio = authors.filter(author => author.bio);
  if (!withBio.length)
    return null;
  return (
    <Box component="aside" sx={{ clear: 'both', mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'surface.n90' }}>
      <Typography component="h2" sx={{ color: 'surface.dark', fontWeight: 700, fontSize: '1.25rem', mb: 2.5 }}>
        {t(withBio.length > 1 ? 'blog.about_authors' : 'blog.about_author')}
      </Typography>
      <Box sx={{ display: 'grid', gap: 2 }}>
        {withBio.map(author => (
          <Box key={author.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', backgroundColor: 'surface.n96', borderRadius: '12px', p: 2.5 }}>
            <AuthorAvatar author={author} size={64} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={NAME_SX}>{author.name}</Typography>
              <Typography variant="body2" sx={{ color: 'surface.contrastText', mb: 1 }}>
                {author.title}
                {author.ocl && <>{' · '}<ProfileLink username={author.ocl} /></>}
              </Typography>
              <Typography variant="body2" sx={{ color: 'surface.dark', lineHeight: 1.6 }}>{author.bio}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
